import React, { useState } from "react";
import ApiProxy from "../../../app/api/admin/proxy/ApiProxy";
import Button from "../../components/button";

// Статус последней проверки (last_check.status). null — ещё не проверялся.
// error — проверка НЕ СМОГЛА дойти до вердикта (таймаут, сбой у нас): о самом прокси это ничего
// не говорит, поэтому серый, а не красный.
// queued / running — в очереди массовой проверки; остальные поля last_check при этом от прошлой.
export const CHECK_STATUSES = {
    'ok': { label: 'Годен', badge: 'badge_online' },
    'failed': { label: 'Не годен', badge: 'badge_offline' },
    'error': { label: 'Не удалось проверить', badge: '' },
    'queued': { label: 'В очереди', badge: 'badge_accent' },
    'running': { label: 'Проверяется', badge: 'badge_accent' },
};

export const CHECK_STATUS_OPTIONS = {
    'ok': 'Годен',
    'failed': 'Не годен',
    'error': 'Не удалось проверить',
    'queued': 'В очереди',
    'running': 'Проверяется',
    'never': 'Не проверялся',
};

// Пока проверка или замер в очереди / идут, перечитываем данные: дока API — раз в 15–60 с.
export const PROXY_POLL_MS = 20000;

const PENDING_STATUSES = ['queued', 'running'];

// Идёт ли у прокси что-то фоновое — проверка из очереди или замер соединений.
export function isProxyPending(proxy) {
    return PENDING_STATUSES.includes(proxy?.last_check?.status)
        || PENDING_STATUSES.includes(proxy?.concurrency?.status);
}

// Чем прокси отказал на ступени замера (concurrency.failure_kind).
export const FAILURE_KINDS = {
    'timeout': 'перегруз кладёт все соединения',
    'reset': 'рвёт лишние соединения',
    'refused': 'не принимает соединения',
    'protocol': 'ошибка протокола',
    'auth': 'не пустил по логину/паролю',
    'fd_limit': 'не хватило ресурсов у нас — число занижено',
    'error': 'прочая ошибка',
};

// Замер одновременных соединений одной строкой: «140 · до 1 устр.». Число — из последнего
// СОСТОЯВШЕГОСЯ замера: при queued / running / error оно остаётся прежним.
export function formatConcurrency(concurrency) {
    const { status, max, devices_capacity: devicesCapacity, assigned_devices: assignedDevices } = concurrency ?? {};
    if (PENDING_STATUSES.includes(status)) return 'замеряется…';
    if (max === null || max === undefined) return status === 'error' ? 'замер не удался' : '—';
    // Потолок замера — 100 соединений (одно устройство с запасом), так что тут «до 1» или «не тянет».
    const capacity = devicesCapacity > 0 ? `до ${devicesCapacity} устр.` : 'не тянет устройство';
    // На прокси уже сидели устройства — замерен остаток, а не потолок.
    const remainderNote = assignedDevices > 0 ? ' (остаток)' : '';
    return `${max}${remainderNote} · ${capacity}`;
}

// Подсказка к замеру: почему не удался или на чём прокси отказал.
export function concurrencyTitle(concurrency) {
    if (!concurrency) return undefined;
    if (concurrency.status === 'error' && concurrency.error) return `Замер не удался: ${concurrency.error}`;
    if (concurrency.failed_at_step) {
        const kind = FAILURE_KINDS[concurrency.failure_kind] ?? concurrency.failure_kind;
        return `Отказал на ${concurrency.failed_at_step} соединениях: ${kind}`;
    }
    return undefined;
}

export function ProxyCheckBadge({ status }) {
    const meta = CHECK_STATUSES[status] ?? { label: 'Не проверялся', badge: '' };
    return <span className={`badge ${meta.badge}`}>{meta.label}</span>;
}

// Да / нет / «—» (шаг не выполнялся: проверка останавливается на первом провале).
export function formatStepOk(value) {
    if (value === null || value === undefined) return '—';
    return value ? 'да' : 'нет';
}

// «Проверить»: ставит прокси в очередь проверки (тот же POST proxies/check, что и массовая) —
// проверяет крон, итог приходит обновлением списка / карточки, пока статус queued / running.
// Синхронная проверка (POST proxies/{id}/check) держала бы запрос до ~4 мин. onDone(data) —
// прокси со статусом «В очереди».
export function ProxyCheckButton({ proxyId, onDone, disabled = false, className = '' }) {
    const [isBusy, setIsBusy] = useState(false);

    const handleCheck = async () => {
        if (isBusy) return;
        setIsBusy(true);
        try {
            const data = await ApiProxy.CheckMany([proxyId]).getResponse();
            const proxy = data?.data?.[0] ?? null;
            if (proxy && onDone) onDone(proxy);
        } finally {
            setIsBusy(false);
        }
    };

    return <Button className={className} disabled={disabled || isBusy} onClick={handleCheck}>
        {isBusy ? 'В очередь…' : 'Проверить'}
    </Button>;
}
